import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import GroupsTable from './GroupsTable';
import ConfirmDialog from './ConfirmDialog';
import GroupVariantsModal from './GroupVariantsModal/GroupVariantsModal';
import CreateGroupModal from './CreateGroupModal';
import EditGroupModal from './GroupVariantsModal/EditGroupModal';
import { getGroups, createGroup, deleteGroup, exportGroups, importGroups, downloadGroupsTemplate } from '../api/groups';
import { GroupInterface } from '../types';
import ImportErrorModal from './ImportErrorModal';

function Main(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const variantsModalOpen = searchParams.get('modal') === 'variants';
  const createModalOpen = searchParams.get('modal') === 'create';
  const editGroupModalOpen = searchParams.get('modal') === 'edit';

  const selectedGroupId = searchParams.get('groupId');
  const editGroupId = searchParams.get('editGroupId');

  const selectedGroup = useMemo(() => groups.find(g => g.id?.toString() === selectedGroupId) || null, [groups, selectedGroupId]);
  const editGroup = useMemo(() => groups.find(g => g.id?.toString() === editGroupId) || null, [groups, editGroupId]);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importErrorsModalOpen, setImportErrorsModalOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const fetchGroups = useCallback(async (): Promise<void> => {
    setLoading(true);
    let queryParams = `page=1&page_size=100`; // Fetch all for now, or just enough
    if (searchQuery) {
      queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
    }
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    if (showInactive) {
      queryParams += `&inactive_groups=true`;
    }
    const response = await getGroups(queryParams);
    setGroups(response.data || []);
    setLoading(false);
  }, [searchQuery, sortBy, sortOrder, showInactive]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleExport = async () => {
    let queryParams = `page=1&page_size=100`;
    if (searchQuery) {
      queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
    }
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    if (showInactive) {
      queryParams += `&inactive_groups=true`;
    }
    await exportGroups(queryParams);
  };

  const handleImport = async (data: any[]) => {
    try {
      await importGroups(data);
      alert('הייבוא הושלם בהצלחה!');
      fetchGroups();
    } catch (err: any) {
      if (err.errors) {
        setImportErrors(err.errors);
        setImportErrorsModalOpen(true);
      } else {
        alert(err.message || 'שגיאה בייבוא הנתונים');
      }
    }
  };

  const handleCreate = async (groupData: Partial<GroupInterface>): Promise<void> => {
    await createGroup(groupData);
    fetchGroups();
  };

  const handleEdit = (id: string): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('modal', 'edit');
    newParams.set('editGroupId', id?.toString());
    setSearchParams(newParams);
  };

  const handleCloseEditGroupModal = (): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    newParams.delete('editGroupId');
    setSearchParams(newParams);
  };

  const handleGroupUpdated = () => {
    fetchGroups();
    handleCloseEditGroupModal();
  };

  const handleDelete = (id: string): void => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    const confirmationCode = prompt('הזן את הקוד לאישור מחיקת הקבוצה');
    if (confirmationCode !== process.env.REACT_APP_DELETE_GROUP_CODE) {
      alert('אין אישור למחוק קבוצה')
      return;
    }
    if (deleteId) {
      await deleteGroup(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
      fetchGroups();
    }
  };

  const handleCancelDelete = (): void => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handleGroupClick = (group: GroupInterface): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('modal', 'variants');
    newParams.set('groupId', group.id?.toString());
    setSearchParams(newParams);
  };

  const handleCloseVariantsModal = (): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    newParams.delete('groupId');
    setSearchParams(newParams);
  };

  const handleOpenCreateModal = (): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('modal', 'create');
    setSearchParams(newParams);
  };

  const handleCloseCreateModal = (): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    setSearchParams(newParams);
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleResetSearch = () => {
    setSearchQuery('');
  };

  return (
    <div style={{ maxWidth: '90%', margin: '20px auto' }}>
      <GroupsTable
        groups={groups}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onGroupClick={handleGroupClick}
        onAdd={handleOpenCreateModal}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        onSearch={setSearchQuery}
        onResetSearch={handleResetSearch}
        initialSearchQuery={searchQuery}
        onExport={handleExport}
        onImport={handleImport}
        onDownloadTemplate={downloadGroupsTemplate}
        showInactive={showInactive}
        onInactiveChange={setShowInactive}
      />
      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <GroupVariantsModal
        open={variantsModalOpen}
        group={selectedGroup}
        onClose={handleCloseVariantsModal}
        onRefresh={fetchGroups}
      />
      <CreateGroupModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onCreate={handleCreate}
      />
      <EditGroupModal
        open={editGroupModalOpen}
        group={editGroup}
        onClose={handleCloseEditGroupModal}
        onGroupUpdated={handleGroupUpdated}
      />
      <ImportErrorModal
        open={importErrorsModalOpen}
        onClose={() => setImportErrorsModalOpen(false)}
        errors={importErrors}
      />
    </div>
  );
}

export default Main; 